import { useQuery } from "@tanstack/react-query";
import { APIClient } from "lib/api/client";
import { useAPIClient } from "render/context/APIContext";
import { ExtractBody, ExtractKey, QueryOptions } from "typings/query";

const queryKey = (
  patientId: string | undefined,
  visitId: string | undefined
) => ["checkInQuestions", patientId, visitId];
const queryFn =
  (
    apiClient: APIClient,
    patientId: string | undefined,
    visitId: string | undefined
  ) =>
  async () => {
    if (patientId == null) {
      return Promise.reject("No patientId");
    }
    if (visitId == null) {
      return Promise.reject("No visitId");
    }
    return apiClient.checkInQuestions.getCheckInQuestionsFor({
      patientId,
      visitId,
    }).result;
  };

type Body = ExtractBody<typeof queryFn>;
type Key = ExtractKey<typeof queryKey>;

// Only required questions count: an optional one the member skipped on their
// phone stays due, but is not something the operator needs to chase.
export function useHasNoOutstandingRequiredCheckInQuestionsQuery<T = Body>({
  patientId,
  visitId,
  options,
}: {
  patientId: string | undefined;
  visitId: string | undefined;
  options: Omit<QueryOptions<Body, Key, T>, "select">;
}) {
  const apiClient = useAPIClient();

  return useQuery({
    queryKey: queryKey(patientId, visitId),
    queryFn: queryFn(apiClient, patientId, visitId),
    select: (data) => !data.questions.some(hasOutstandingRequiredQuestion),
    ...options,
    enabled:
      Boolean(patientId) && Boolean(visitId) && (options.enabled ?? true),
  });
}

export function hasOutstandingRequiredQuestion(
  checkInQuestion: Body["questions"][number]
): boolean {
  return checkInQuestion.question.isRequired;
}
